import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { JSX } from "react";

import { PromotionsPage, type PromotionFilters } from "@/features/promotion";

import { rememberListSearch } from "./-promotion-list-search";

type Marketplace = "shopee" | "lazada" | "tiktok";
type PromotionStatus = "draft" | "active" | "finish" | "archived";

interface PromotionSearch {
  campaign_id?: string;
  status?: PromotionStatus;
  marketplace?: Marketplace;
  search?: string;
}

const STATUSES: PromotionStatus[] = ["draft", "active", "finish", "archived"];
const MARKETPLACES: Marketplace[] = ["shopee", "lazada", "tiktok"];

function str(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

/** Only the keys `validateSearch` reads go to the URL; seller/date stay page-local. */
function toListSearch(f: PromotionFilters): PromotionSearch {
  const out: PromotionSearch = {};
  if (f.campaign_id) out.campaign_id = f.campaign_id;
  if (f.status && STATUSES.includes(f.status as PromotionStatus))
    out.status = f.status as PromotionStatus;
  if (f.marketplace && MARKETPLACES.includes(f.marketplace as Marketplace))
    out.marketplace = f.marketplace as Marketplace;
  if (f.search) out.search = f.search;
  return out;
}

function PromotionRoute(): JSX.Element {
  const navigate = useNavigate();
  const search = Route.useSearch();
  // Intentionally runs every render (no useEffect — routes are routing only, CLAUDE.md
  // rule 1); it's a cheap idempotent assignment, so re-running it on re-renders is harmless.
  rememberListSearch(search);
  return (
    <PromotionsPage
      initialFilters={search}
      onEditPromotion={(promotion) =>
        void navigate({ to: "/promotion/$promotionId", params: { promotionId: promotion.id } })
      }
      onPromotionCreated={(id) =>
        void navigate({ to: "/promotion/$promotionId", params: { promotionId: id } })
      }
      // replace: filter tweaks are not history entries; Back from a promotion lands on this exact list URL.
      onFiltersChange={(f) => {
        const next = toListSearch(f);
        rememberListSearch(next);
        void navigate({ to: "/promotion", search: next, replace: true });
      }}
    />
  );
}

export const Route = createFileRoute("/_authenticated/promotion")({
  // Typed search → seeds PromotionsPage filters (e.g. campaign drill-in).
  // Only present keys are emitted (exactOptionalPropertyTypes-friendly).
  validateSearch: (raw: Record<string, unknown>): PromotionSearch => {
    const status = str(raw.status);
    const marketplace = str(raw.marketplace);
    const campaignId = str(raw.campaign_id);
    const searchText = str(raw.search);
    const out: PromotionSearch = {};
    if (campaignId) out.campaign_id = campaignId;
    if (STATUSES.includes(status as PromotionStatus)) out.status = status as PromotionStatus;
    if (MARKETPLACES.includes(marketplace as Marketplace))
      out.marketplace = marketplace as Marketplace;
    if (searchText) out.search = searchText;
    return out;
  },
  component: PromotionRoute,
});
